import React, { useCallback, useEffect, useRef, useState } from "react";
import { io } from "socket.io-client";
import { Send, Volume2, Square } from "lucide-react";
import "../../assets/css/AskAI.css";
import { getProjectPid } from "../../utils/sessionProject";
import { getPromptByType } from "../../services/promptService";
import LangSelect from "./LangSelect";

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || process.env.REACT_APP_BASE_URL;

const FALLBACK_GREETING = "Welcome! 🚀 I’m here to answer any questions you may have. How can I assist you today?";

// ── Text Chat Page ───────────────────────────────────────────────────────────
const AskAiText = () => {
  const token = localStorage.getItem("token");
  const projectPid = getProjectPid();

  const [messages, setMessages] = useState([
    { role: "assistant", text: FALLBACK_GREETING },
  ]);

  // ── Load dynamic welcome message ─────────────────────────────────────────
  useEffect(() => {
    if (!projectPid) return;
    getPromptByType({ projectPid, promptType: "welcome_message" })
      .then((data) => {
        const content = data?.response?.content;
        if (content) setMessages([{ role: "assistant", text: content }]);
      })
      .catch(() => {});
  }, [projectPid]);
  const [input, setInput] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingText, setStreamingText] = useState("");
  const [chatStatus, setChatStatus] = useState("");
  const [language, setLanguage] = useState("en");
  const [speakingId, setSpeakingId] = useState(null);
  const [socketConnected, setSocketConnected] = useState(false);
  const [socketError, setSocketError] = useState("");

  const chatRef = useRef(null);
  const socketRef = useRef(null);
  const streamingRef = useRef("");

  // ── Connect socket ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!token || !projectPid) return;
    const socket = io(SOCKET_URL, {
      transports: ["websocket", "polling"],
      auth: { token, projectPid },
    });
    socketRef.current = socket;

    socket.on("connect", () => { setSocketConnected(true); setSocketError(""); });
    socket.on("connect_error", (err) => { setSocketError(err.message); setSocketConnected(false); });
    socket.on("disconnect", () => setSocketConnected(false));

    socket.on("chat:status", ({ text }) => setChatStatus(text));

    socket.on("chat:token", ({ text }) => {
      streamingRef.current += text;
      setStreamingText(streamingRef.current);
    });

    socket.on("chat:done", ({ fullText }) => {
      setMessages(prev => [...prev, { role: "assistant", text: fullText || streamingRef.current }]);
      streamingRef.current = "";
      setStreamingText("");
      setChatStatus("");
      setIsStreaming(false);
    });

    socket.on("chat:error", () => {
      setChatStatus("");
      setIsStreaming(false);
      streamingRef.current = "";
      setStreamingText("");
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [token, projectPid]);

  useEffect(() => {
    if (chatRef.current) chatRef.current.scrollTop = chatRef.current.scrollHeight;
  }, [messages, streamingText, chatStatus]);

  const sendMessage = useCallback((customText = null) => {
    const text = (customText || input).trim();
    if (!text || isStreaming) return;
    setMessages(prev => [...prev, { role: "user", text }]);
    setInput("");
    setIsStreaming(true);
    streamingRef.current = "";
    setStreamingText("");
    socketRef.current?.emit("chat:message", { message: text, language });
  }, [input, isStreaming, language]);

  const cancelMessage = () => {
    socketRef.current?.emit("chat:cancel");
    setIsStreaming(false);
    streamingRef.current = "";
    setStreamingText("");
    setChatStatus("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  const speakText = (text, id) => {
    if (window.speechSynthesis.speaking) { window.speechSynthesis.cancel(); setSpeakingId(null); return; }
    const utter = new SpeechSynthesisUtterance(text);
    utter.onend = () => setSpeakingId(null);
    setSpeakingId(id);
    window.speechSynthesis.speak(utter);
  };

  return (
    <main className="ask-ai-page">
      <div className="ai-chat-wrapper">

        {/* Messages */}
        <div className="ai-chat-messages" ref={chatRef}>
          {messages.map((msg, index) => (
            <div key={index} className={`message-row ${msg.role === "user" ? "user-message-row" : "ai-message-row"}`}>
              <div className="message-content">
                <div className={`message-bubble ${msg.role === "user" ? "user-bubble" : "ai-bubble"}`}>
                  {msg.text}
                </div>
                {msg.role === "assistant" && (
                  <div className="msg-meta">
                    <button className="read-aloud-btn" onClick={() => speakText(msg.text, index)}>
                      {speakingId === index ? <><Square size={13} />Stop</> : <><Volume2 size={13} />Read aloud</>}
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}

          {/* Streaming bubble */}
          {isStreaming && (
            <div className="message-row ai-message-row">
              <div className="message-content">
                <div className="message-bubble ai-bubble">
                  {streamingText ? (
                    <span>{streamingText}<span className="streaming-cursor" /></span>
                  ) : (
                    <span className="typing-wrap">
                      <span className="typing-dot" /><span className="typing-dot" /><span className="typing-dot" />
                    </span>
                  )}
                </div>
                {chatStatus && <p className="chat-status-text">{chatStatus}</p>}
              </div>
            </div>
          )}
        </div>

        {/* Socket status — only show if never connected */}
        {!socketConnected && socketError && (
          <div className="ai-socket-error">
            ⚠ Connection error: {socketError}
          </div>
        )}

        {/* Input bar */}
        <div className="ai-chat-input-area">
          <div className="ai-input-wrapper">
            <textarea
              rows="1"
              className="ai-textarea"
              placeholder="Message AI assistant…"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
            />
            <LangSelect value={language} onChange={setLanguage} />
            {isStreaming ? (
              <button className="send-btn cancel-btn" onClick={cancelMessage} title="Cancel">
                <Square size={16} />
              </button>
            ) : (
              <button className="send-btn" onClick={() => sendMessage()} disabled={!input.trim()}>
                <Send size={18} />
              </button>
            )}
          </div>
          <p className="ai-disclaimer">AI responses may be inaccurate. Verify important information independently.</p>
        </div>
      </div>
    </main>
  );
};

export default AskAiText;
